/**
 * Colour maths for the console themes' guards (spec docs/superpowers/specs/2026-09-27-admin-console-themes-design.md,
 * "Contrast rules" and "Data colours"): read the colours admin-theme.css writes, convert them to
 * sRGB, and measure what WCAG and the dataviz method measure.
 *
 *   - OKLab / OKLCH: Björn Ottosson (2020), the matrices CSS Color 4 uses.
 *   - Contrast: WCAG 2 relative luminance and ratio.
 *   - Colour-vision deficiency: Machado, Oliveira & Fernandes (2009) at severity 1.0 — the model the
 *     dataviz thresholds (ΔE 8 target, 6 floor) are calibrated to.
 *
 * Pure functions, no DOM: the specs run them over the stylesheet's own text.
 */

/** sRGB, gamma-encoded, each channel 0..1. */
export type Rgb = readonly [number, number, number]

export interface CssColour {
  /** sRGB, clipped to 0..1 when the colour lies outside sRGB. */
  rgb: Rgb
  /** 0..1 */
  alpha: number
  /** False when an oklch() value lies outside sRGB — its rgb is then the clipped approximation. */
  inGamut: boolean
  /**
   * The relative luminance a browser may paint. One value for a colour inside sRGB; for one
   * outside it, three — the colour itself (a wide-gamut screen), clipped per channel, and
   * gamut-mapped by lowering chroma at the same lightness and hue (CSS Color 4) — because which
   * one a viewer gets depends on their screen and browser.
   */
  luminances: number[]
}

export type Vision = 'protan' | 'deutan' | 'tritan'

const decode = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
const encode = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055)
const clamp01 = (c: number) => Math.min(1, Math.max(0, c))

/** A linear channel this close outside 0..1 is rounding, not a colour outside sRGB. */
const GAMUT_EPSILON = 1e-4

export function hexToRgb(hex: string): Rgb {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) throw new Error(`Not a colour this guard can read: "${hex}" (write #rrggbb)`)
  const h = m[1].length === 3 ? [...m[1]].map((d) => d + d).join('') : m[1]
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as unknown as Rgb
}

export function rgbToHex(rgb: Rgb): string {
  return `#${rgb.map((c) => Math.round(clamp01(c) * 255).toString(16).padStart(2, '0')).join('')}`
}

/** OKLCH → linear-light sRGB, unclipped (a channel outside 0..1 means the colour lies outside sRGB). */
function oklchToLinear(l: number, c: number, h: number): [number, number, number] {
  const hr = (h * Math.PI) / 180
  const a = c * Math.cos(hr)
  const b = c * Math.sin(hr)
  const l_ = l + 0.3963377774 * a + 0.2158037573 * b
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b
  const s_ = l - 0.0894841775 * a - 1.291485548 * b
  const [L, M, S] = [l_ ** 3, m_ ** 3, s_ ** 3]
  return [
    4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
    -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
    -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S,
  ]
}

const insideSrgb = (linear: readonly number[]) => linear.every((v) => v >= -GAMUT_EPSILON && v <= 1 + GAMUT_EPSILON)
const linearLuminance = ([r, g, b]: readonly number[]) => 0.2126 * r + 0.7152 * g + 0.0722 * b

/** OKLCH → sRGB. `inGamut` is false when a linear channel falls outside 0..1 (the rgb is then clipped). */
export function oklchToRgb(l: number, c: number, h: number): { rgb: Rgb; inGamut: boolean } {
  const linear = oklchToLinear(l, c, h)
  return { rgb: linear.map((v) => encode(clamp01(v))) as unknown as Rgb, inGamut: insideSrgb(linear) }
}

/** The luminances an oklch() colour may be painted with (see CssColour.luminances). */
function oklchLuminances(l: number, c: number, h: number): number[] {
  const linear = oklchToLinear(l, c, h)
  if (insideSrgb(linear)) return [linearLuminance(linear.map(clamp01))]
  // Gamut mapping, CSS Color 4 style: the largest chroma at this lightness and hue that fits.
  let [lo, hi] = [0, c]
  for (let k = 0; k < 40; k++) {
    const mid = (lo + hi) / 2
    if (insideSrgb(oklchToLinear(l, mid, h))) lo = mid
    else hi = mid
  }
  return [linearLuminance(linear), linearLuminance(linear.map(clamp01)), linearLuminance(oklchToLinear(l, lo, h).map(clamp01))]
}

function oklabFromLinear([r, g, b]: readonly number[]): [number, number, number] {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

export function rgbToOklab(rgb: Rgb): [number, number, number] {
  return oklabFromLinear(rgb.map(decode))
}

/** sRGB → OKLCH: [L 0..1, C, hue in degrees 0..360). */
export function rgbToOklch(rgb: Rgb): [number, number, number] {
  const [l, a, b] = rgbToOklab(rgb)
  return [l, Math.hypot(a, b), (((Math.atan2(b, a) * 180) / Math.PI) % 360 + 360) % 360]
}

const NUMBER = String.raw`[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?`
const OKLCH = new RegExp(
  String.raw`^oklch\(\s*(${NUMBER})(%?)\s+(${NUMBER})\s+(${NUMBER})(?:deg)?\s*(?:\/\s*(${NUMBER})(%?)\s*)?\)$`,
  'i',
)

/**
 * The two colour forms the console stylesheet writes: `#rgb` / `#rrggbb`, and `oklch(L C H)` with
 * L as a number or a percentage and an optional `/ alpha` (number or percentage). Anything else
 * throws — a guard that cannot read a value must say so, never skip it.
 */
export function parseCssColour(value: string): CssColour {
  const v = value.trim()
  if (v.startsWith('#')) {
    const rgb = hexToRgb(v)
    return { rgb, alpha: 1, inGamut: true, luminances: [relativeLuminance(rgb)] }
  }
  const m = OKLCH.exec(v)
  if (!m) throw new Error(`Not a colour this guard can read: "${value}" (write #rrggbb or oklch(L C H [/ A]))`)
  const [l, c, h] = [Number(m[1]) / (m[2] ? 100 : 1), Number(m[3]), Number(m[4])]
  const alpha = m[5] === undefined ? 1 : Number(m[5]) / (m[6] ? 100 : 1)
  const { rgb, inGamut } = oklchToRgb(l, c, h)
  return { rgb, alpha, inGamut, luminances: oklchLuminances(l, c, h) }
}

export function relativeLuminance(rgb: Rgb): number {
  return linearLuminance(rgb.map(decode))
}

const ratioOf = (y1: number, y2: number) => (Math.max(y1, y2) + 0.05) / (Math.min(y1, y2) + 0.05)

/** WCAG 2 contrast ratio, 1..21, whichever colour is lighter. */
export function contrastRatio(x: Rgb, y: Rgb): number {
  return ratioOf(relativeLuminance(x), relativeLuminance(y))
}

/**
 * WCAG contrast of a foreground over an opaque background, as CSS writes them, taking the worst
 * of the ways a browser may paint a colour outside sRGB. A translucent foreground is composited
 * over the background first; a translucent background is refused, because the pair would then
 * depend on whatever lies beneath it.
 */
export function worstContrast(foreground: string, background: string): number {
  const [fg, bg] = [parseCssColour(foreground), parseCssColour(background)]
  if (bg.alpha !== 1) throw new Error(`The background of a contrast pair must be opaque: "${background}"`)
  if (fg.alpha !== 1) return contrastRatio(composite(fg.rgb, fg.alpha, bg.rgb), bg.rgb)
  return Math.min(...fg.luminances.flatMap((y1) => bg.luminances.map((y2) => ratioOf(y1, y2))))
}

/** `top` at `alpha` over an opaque `surface`, mixed in encoded sRGB — what a browser paints for bg-[#hex]/NN. */
export function composite(top: Rgb, alpha: number, surface: Rgb): Rgb {
  if (alpha >= 1) return top
  return top.map((c, i) => c * alpha + surface[i] * (1 - alpha)) as unknown as Rgb
}

// Machado, Oliveira & Fernandes (2009), severity 1.0, on linear sRGB.
const MACHADO: Record<Vision, number[][]> = {
  protan: [
    [0.152286, 1.052583, -0.204868],
    [0.114503, 0.786281, 0.099216],
    [-0.003882, -0.048116, 1.051998],
  ],
  deutan: [
    [0.367322, 0.860646, -0.227968],
    [0.280085, 0.672501, 0.047413],
    [-0.01182, 0.04294, 0.968881],
  ],
  tritan: [
    [1.255528, -0.076749, -0.178779],
    [-0.078411, 0.930809, 0.147602],
    [0.004733, 0.691367, 0.3039],
  ],
}

function linearFor(rgb: Rgb, vision?: Vision): number[] {
  const lin = rgb.map(decode)
  if (!vision) return lin
  return MACHADO[vision].map((row) => clamp01(row[0] * lin[0] + row[1] * lin[1] + row[2] * lin[2]))
}

/** Euclidean distance in OKLab ×100 — under normal vision, or as a protanope/deuteranope/tritanope sees the pair. */
export function deltaE(x: Rgb, y: Rgb, vision?: Vision): number {
  const [a, b] = [oklabFromLinear(linearFor(x, vision)), oklabFromLinear(linearFor(y, vision))]
  return 100 * Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])
}
